import { getScheduleLocation } from '../../../utils/shipToLocation';
import React from 'react';
import { getCompletionStatus, isWeekBoundaryDate, isOutsideCurrentYear } from './highlightingUtils';
// Import centralized date utilities
import { formatDateToMMDD } from '../../../utils/dateUtils';
import { hasLongBuildPumps } from './dataProcessing';

// Helper function to check if a due date is past due and not completed
const isPastDueAndNotCompleted = (dueDate, doneDate) => {
  if (!dueDate) return false;
  
  // Consider it incomplete if doneDate is null, empty, whitespace, or "R"
  const isIncomplete = !doneDate || 
                      (typeof doneDate === 'string' && (doneDate.trim() === '' || doneDate.trim().toUpperCase() === 'R'));
  
  if (!isIncomplete) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  
  return due < today;
};

// Helper function to render due date with checkmark if done date exists
const renderDueDateWithCheckmark = (dueDate, doneDate) => {
  const formattedDate = formatDateToMMDD(dueDate) || "";
  const hasDoneDate = doneDate && doneDate.trim() !== "";
  
  return (
    <>
      {formattedDate}
      {hasDoneDate && (
        <i className="bi bi-check ms-1" style={{ color: "green" }} title="Task completed"></i>
      )}
    </>
  );
};

// Engineering columns renderer
export const renderEngineeringColumns = (job, filters, handleCellDoubleClick, renderEditableCell, pumpDetails = []) => {
  if (!filters.departments.engineering) return null;
  const isControlPanelView = Boolean(filters.controlPanelView);
  const isNorthPrairieView = Boolean(filters.northPrairieView);
  
  if (filters.dueDatesOnly) {
    return (
      <>
        {!isControlPanelView && (
          <td className={`editable-cell ${isPastDueAndNotCompleted(job.MechanicalDue, job.MechanicalDone) ? 'highlight-high-priority' : ''}`} 
              onDoubleClick={(e) => handleCellDoubleClick(e, job, "MechanicalDue")}>
            {renderEditableCell(job, "MechanicalDue", renderDueDateWithCheckmark(job.MechanicalDue, job.MechanicalDone), pumpDetails)}
          </td>
        )}
        {!isNorthPrairieView && (
          <td className={`editable-cell ${isPastDueAndNotCompleted(job.ElectricalDue, job.ElectricalDone) ? 'highlight-high-priority' : ''}`} 
              onDoubleClick={(e) => handleCellDoubleClick(e, job, "ElectricalDue")}>
            {renderEditableCell(job, "ElectricalDue", renderDueDateWithCheckmark(job.ElectricalDue, job.ElectricalDone), pumpDetails)}
          </td>
        )}
      </>
    );
  }
  return (
    <>
      {!isControlPanelView && (
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "MechanicalEngineer")}>
          {renderEditableCell(job, "MechanicalEngineer", job.MechanicalEngineer || "", pumpDetails)}
        </td>
      )}
      {!isNorthPrairieView && (
        <>
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ElectricalEngineer")}>
            {renderEditableCell(job, "ElectricalEngineer", job.ElectricalEngineer || "", pumpDetails)}
          </td>
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ControlsEngineer")}>
            {renderEditableCell(job, "ControlsEngineer", job.ControlsEngineer || "", pumpDetails)}
          </td>
        </>
      )}
      {!isControlPanelView && (
        <>
          <td className={`editable-cell ${isPastDueAndNotCompleted(job.MechanicalDue, job.MechanicalDone) ? 'highlight-high-priority' : ''}`} 
              onDoubleClick={(e) => handleCellDoubleClick(e, job, "MechanicalDue")}>
            {renderEditableCell(job, "MechanicalDue", formatDateToMMDD(job.MechanicalDue) || "", pumpDetails)}
          </td>
          <td className={`editable-cell ${getCompletionStatus(job.MechanicalDone, job.MechanicalDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "MechanicalDone")}>
            {renderEditableCell(job, "MechanicalDone", formatDateToMMDD(job.MechanicalDone) || "", pumpDetails)}
          </td>
        </>
      )}
      {!isNorthPrairieView && (
        <>
          <td className={`editable-cell ${isPastDueAndNotCompleted(job.ElectricalDue, job.ElectricalDone) ? 'highlight-high-priority' : ''}`} 
              onDoubleClick={(e) => handleCellDoubleClick(e, job, "ElectricalDue")}>
            {renderEditableCell(job, "ElectricalDue", formatDateToMMDD(job.ElectricalDue) || "", pumpDetails)}
          </td>
          <td className={`editable-cell ${getCompletionStatus(job.ElectricalDone, job.ElectricalDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "ElectricalDone")}>
            {renderEditableCell(job, "ElectricalDone", formatDateToMMDD(job.ElectricalDone) || "", pumpDetails)}
          </td>
        </>
      )}
    </>
  );
};

// Fabrication columns renderer
export const renderFabricationColumns = (job, filters, handleCellDoubleClick, renderEditableCell) => {
  if (!filters.departments.fabrication) return null;
  const isControlPanelView = Boolean(filters.controlPanelView);

    if (filters.dueDatesOnly) {
    return (
      <>
        {!isControlPanelView && (
          <>
            <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPPipeFabDue")}>
              {renderEditableCell(job, "NPPipeFabDue", renderDueDateWithCheckmark(job.NPPipeFabDue, job.NPPipeFabDone))}
            </td>
            <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "FusionBondRequired")}>
              {renderEditableCell(job, "FusionBondRequired", job.FusionBondRequired || "")}
            </td>
          </>
        )}
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "FabDue")}>
          {renderEditableCell(job, "FabDue", renderDueDateWithCheckmark(job.FabDue, job.FabDone))}
        </td>
      </>
    );
  }
  
  return (
    <>
      {!isControlPanelView && (
        <>
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPPipeFabDue")}>
            {renderEditableCell(job, "NPPipeFabDue", formatDateToMMDD(job.NPPipeFabDue) || "")}
          </td>
          <td className={`editable-cell ${getCompletionStatus(job.NPPipeFabDone, job.NPPipeFabDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPPipeFabDone")}>
            {renderEditableCell(job, "NPPipeFabDone", formatDateToMMDD(job.NPPipeFabDone) || "")}
          </td>
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "FusionBondRequired")}>
            {renderEditableCell(job, "FusionBondRequired", job.FusionBondRequired || "")}
          </td>
        </>
      )}
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "FabDue")}>
        {renderEditableCell(job, "FabDue", formatDateToMMDD(job.FabDue) || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.FabDone, job.FabDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "FabDone")}>
        {renderEditableCell(job, "FabDone", formatDateToMMDD(job.FabDone) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "FabExpectedCompletion")}>
        {renderEditableCell(job, "FabExpectedCompletion", formatDateToMMDD(job.FabExpectedCompletion) || "")}
      </td>
    </>
  );
};

// Control Panel columns renderer
export const renderControlPanelColumns = (job, filters, handleCellDoubleClick, renderEditableCell) => {
  if (!filters.departments.controlPanel) return null;
  if (filters.northPrairieView) return null;
  
  if (filters.dueDatesOnly) {
    return (
      <>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "SoftwareDue")}>
          {renderEditableCell(job, "SoftwareDue", renderDueDateWithCheckmark(job.SoftwareDue, job.SoftwareDone))}
        </td>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ControlPanelDue")}>
          {renderEditableCell(job, "ControlPanelDue", renderDueDateWithCheckmark(job.ControlPanelDue, job.ControlPanelDone))}
        </td>
      </>
    );
  }
  
  return (
    <>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "SoftwareDue")}>
        {renderEditableCell(job, "SoftwareDue", formatDateToMMDD(job.SoftwareDue) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "SoftwareType")}>
        {renderEditableCell(job, "SoftwareType", job.SoftwareType || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.SoftwareDone, job.SoftwareDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "SoftwareDone")}>
        {renderEditableCell(job, "SoftwareDone", formatDateToMMDD(job.SoftwareDone) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ControlPanelDue")}>
        {renderEditableCell(job, "ControlPanelDue", formatDateToMMDD(job.ControlPanelDue) || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.ControlPanelDone, job.ControlPanelDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "ControlPanelDone")}>
        {renderEditableCell(job, "ControlPanelDone", formatDateToMMDD(job.ControlPanelDone) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ControlPanelExpectedCompletion")}>
        {renderEditableCell(job, "ControlPanelExpectedCompletion", formatDateToMMDD(job.ControlPanelExpectedCompletion) || "")}
      </td>
    </>
  );
};

// Pumps columns renderer
export const renderPumpsColumns = (job, filters, handleCellDoubleClick, renderEditableCell, pumpDetails) => {
  if (!filters.departments.pumps) return null;
  
  // Check if this job has long build pumps
  const longBuildResult = hasLongBuildPumps(job, pumpDetails);
  const hasLongBuild = longBuildResult.hasLongBuild;
  const tplValue = longBuildResult.tplValue;
  
  if (filters.dueDatesOnly) {
    return (
      <>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "HCPumpsDue")}>
          {renderEditableCell(job, "HCPumpsDue", renderDueDateWithCheckmark(job.HCPumpsDue, job.HCPumpsDone))}
        </td>
        <td className={`editable-cell ${hasLongBuild ? 'highlight-long-build' : ''}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "VTPumpsDue")}>
          {renderEditableCell(job, "VTPumpsDue", 
            <>
              {renderDueDateWithCheckmark(job.VTPumpsDue, job.PumpDone)}
              {hasLongBuild && (
                <i
                  className="bi bi-arrows-vertical ms-1"
                  style={{ color: "orange" }}
                  title={`LB - ${tplValue}"`}
                ></i>
              )}
            </>
          )}
        </td>
      </>
    );
  }
  
  return (
    <>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "HCPumpsDue")}>
        {renderEditableCell(job, "HCPumpsDue", formatDateToMMDD(job.HCPumpsDue) || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.HCPumpsDone, job.HCPumpsDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "HCPumpsDone")}>
        {renderEditableCell(job, "HCPumpsDone", formatDateToMMDD(job.HCPumpsDone) || "")}
      </td>
      <td className={`editable-cell ${hasLongBuild ? 'highlight-long-build' : ''}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "VTPumpsDue")}>
        {renderEditableCell(job, "VTPumpsDue", 
          <>
            {formatDateToMMDD(job.VTPumpsDue) || ""}
            {hasLongBuild && (
              <i
                className="bi bi-arrows-vertical ms-1"
                style={{ color: "orange" }}
                title={`LB - ${tplValue}"`}
              ></i>
            )}
          </>
        )}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.PumpDone, job.VTPumpsDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "PumpDone")}>
        {renderEditableCell(job, "PumpDone", formatDateToMMDD(job.PumpDone) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "PumpExpectedCompletion")}>
        {renderEditableCell(job, "PumpExpectedCompletion", formatDateToMMDD(job.PumpExpectedCompletion) || "")}
      </td>
    </>
  );
};

// Assembly columns renderer
export const renderAssemblyColumns = (job, filters, handleCellDoubleClick, renderEditableCell) => {
  if (!filters.departments.assembly) return null;
  const isNorthPrairieView = Boolean(filters.northPrairieView);
  
  if (filters.dueDatesOnly) {
    return (
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "AssemblyDue")}>
        {renderEditableCell(job, "AssemblyDue", renderDueDateWithCheckmark(job.AssemblyDue, job.AssemblyDone))}
      </td>
    );
  }
  
  return (
    <>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "AssemblyDue")}>
        {renderEditableCell(job, "AssemblyDue", formatDateToMMDD(job.AssemblyDue) || "")}
      </td>
      {!isNorthPrairieView && (
        <td className={`editable-cell ${getCompletionStatus(job.AssemblyDone, job.AssemblyDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "AssemblyDone")}>
          {renderEditableCell(job, "AssemblyDone", formatDateToMMDD(job.AssemblyDone) || "")}
        </td>
      )}
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "AssemblyExpectedCompletion")}>
        {renderEditableCell(job, "AssemblyExpectedCompletion", formatDateToMMDD(job.AssemblyExpectedCompletion) || "")}
      </td>
    </>
  );
};

// Test columns renderer
export const renderTestColumns = (job, filters, handleCellDoubleClick, renderEditableCell) => {
  if (!filters.departments.test) return null;
  if (filters.northPrairieView) return null;
  
  if (filters.dueDatesOnly) {
    return (
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "TestDue")}>
        {renderEditableCell(job, "TestDue", renderDueDateWithCheckmark(job.TestDue, job.TestDone))}
      </td>
    );
  }
  
  return (
    <>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "TestDue")}>
        {renderEditableCell(job, "TestDue", formatDateToMMDD(job.TestDue) || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.TestDone, job.TestDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "TestDone")}>
        {renderEditableCell(job, "TestDone", formatDateToMMDD(job.TestDone) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "TestExpectedCompletion")}>
        {renderEditableCell(job, "TestExpectedCompletion", formatDateToMMDD(job.TestExpectedCompletion) || "")}
      </td>
    </>
  );
};

// North Prairie columns renderer
export const renderNorthPrairieColumns = (job, filters, handleCellDoubleClick, renderEditableCell) => {
  if (!filters.departments.northPrairie) return null;
  
  // Hide NP Pipe columns when Fabrication filter is active to prevent duplication
  const hideNPPipeColumns = filters.departments.fabrication;
  
  if (filters.dueDatesOnly) {
    return (
      <>
        {!hideNPPipeColumns && (
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPPipeFabDue")}>
            {renderEditableCell(job, "NPPipeFabDue", renderDueDateWithCheckmark(job.NPPipeFabDue, job.NPPipeFabDone))}
          </td>
        )}
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPCSMDue")}>
          {renderEditableCell(job, "NPCSMDue", renderDueDateWithCheckmark(job.NPCSMDue, job.NPCSMDone))}
        </td>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPSuctionLineDue")}>
          {renderEditableCell(job, "NPSuctionLineDue", renderDueDateWithCheckmark(job.NPSuctionLineDue, job.NPSuctionLineDone))}
        </td>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPInducerTubeDue")}>
          {renderEditableCell(job, "NPInducerTubeDue", renderDueDateWithCheckmark(job.NPInducerTubeDue, job.NPInducerTubeDone))}
        </td>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPBMXDue")}>
          {renderEditableCell(job, "NPBMXDue", renderDueDateWithCheckmark(job.NPBMXDue, job.NPBMXDone))}
        </td>
      </>
    );
  }
  
  return (
    <>
      {!hideNPPipeColumns && (
        <>
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPPipeFabDue")}>
            {renderEditableCell(job, "NPPipeFabDue", formatDateToMMDD(job.NPPipeFabDue) || "")}
          </td>
          <td className={`editable-cell ${getCompletionStatus(job.NPPipeFabDone, job.NPPipeFabDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPPipeFabDone")}>
            {renderEditableCell(job, "NPPipeFabDone", formatDateToMMDD(job.NPPipeFabDone) || "")}
          </td>
        </>
      )}
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPCSMDue")}>
        {renderEditableCell(job, "NPCSMDue", formatDateToMMDD(job.NPCSMDue) || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.NPCSMDone, job.NPCSMDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPCSMDone")}>
        {renderEditableCell(job, "NPCSMDone", formatDateToMMDD(job.NPCSMDone) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPSuctionLineDue")}>
        {renderEditableCell(job, "NPSuctionLineDue", formatDateToMMDD(job.NPSuctionLineDue) || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.NPSuctionLineDone, job.NPSuctionLineDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPSuctionLineDone")}>
        {renderEditableCell(job, "NPSuctionLineDone", formatDateToMMDD(job.NPSuctionLineDone) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPInducerTubeDue")}>
        {renderEditableCell(job, "NPInducerTubeDue", formatDateToMMDD(job.NPInducerTubeDue) || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.NPInducerTubeDone, job.NPInducerTubeDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPInducerTubeDone")}>
        {renderEditableCell(job, "NPInducerTubeDone", formatDateToMMDD(job.NPInducerTubeDone) || "")}
      </td>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPBMXDue")}>
        {renderEditableCell(job, "NPBMXDue", formatDateToMMDD(job.NPBMXDue) || "")}
      </td>
      <td className={`editable-cell ${getCompletionStatus(job.NPBMXDone, job.NPBMXDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "NPBMXDone")}>
        {renderEditableCell(job, "NPBMXDone", formatDateToMMDD(job.NPBMXDone) || "")}
      </td>
    </>
  );
};

// Shipping columns renderer
export const renderShippingColumns = (job, filters, handleCellDoubleClick, renderEditableCell, weekColumns) => {
  if (!filters.departments.shipping) return null;
  const isNorthPrairieView = Boolean(filters.northPrairieView);

  if (filters.dueDatesOnly) {
    return (
      <>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "PackDue")}>
          {renderEditableCell(job, "PackDue", formatDateToMMDD(job.PackDue) || "")}
        </td>
        {!isNorthPrairieView && (
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "TransportType")}>
            {renderEditableCell(job, "TransportType", job.TransportType || "")}
          </td>
        )}
        {!filters.hideCurrentPlanShipDate && (
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "CurrentPlanShipDate")}>
            {renderEditableCell(job, "CurrentPlanShipDate",
              <>
                {formatDateToMMDD(job.CurrentPlanShipDate) || ""}
                {isWeekBoundaryDate(job.CurrentPlanShipDate, weekColumns) === 'start' && (
                  <i
                    className="bi bi-calendar-x ms-1"
                    style={{ color: "red" }}
                    title="Date is a Sunday"
                  />
                )}
                {isWeekBoundaryDate(job.CurrentPlanShipDate, weekColumns) === 'end' && (
                  <i
                    className="bi bi-calendar-x ms-1"
                    style={{ color: "red" }}
                    title="Date is a Saturday"
                  />
                )}
                {isOutsideCurrentYear(job.CurrentPlanShipDate) && (
                  <i
                    className="bi bi-calendar-event ms-1"
                    style={{ color: "orange" }}
                    title="Date is outside the current year"
                  />
                )}
              </>
            )}
          </td>
        )}
        {!isNorthPrairieView && (
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ScheduledArrivalDate")}>
            {renderEditableCell(job, "ScheduledArrivalDate", formatDateToMMDD(job.ScheduledArrivalDate) || "")}
          </td>
        )}
      </>
    );
  }

  return (
    <>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "PackDue")}>
        {renderEditableCell(job, "PackDue", formatDateToMMDD(job.PackDue) || "")}
      </td>
      {!isNorthPrairieView && (
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "TransportType")}>
          {renderEditableCell(job, "TransportType", job.TransportType || "")}
        </td>
      )}
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "OriginalScheduledShipDate")}>
        {renderEditableCell(job, "OriginalScheduledShipDate", formatDateToMMDD(job.OriginalScheduledShipDate) || "")}
      </td>
      {!filters.hideCurrentPlanShipDate && (
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "CurrentPlanShipDate")}>
          {renderEditableCell(job, "CurrentPlanShipDate", 
            <>
              {formatDateToMMDD(job.CurrentPlanShipDate) || ""}
              {isWeekBoundaryDate(job.CurrentPlanShipDate, weekColumns) === 'start' && (
                <i 
                  className="bi bi-calendar-x ms-1" 
                  style={{ color: "red" }} 
                  title="Date is a Sunday"
                />
              )}
              {isWeekBoundaryDate(job.CurrentPlanShipDate, weekColumns) === 'end' && (
                <i 
                  className="bi bi-calendar-x ms-1" 
                  style={{ color: "red" }} 
                  title="Date is a Saturday"
                />
              )}
              {isOutsideCurrentYear(job.CurrentPlanShipDate) && (
                <i 
                  className="bi bi-calendar-event ms-1" 
                  style={{ color: "orange" }} 
                  title="Date is outside the current year"
                />
              )}
            </>
          )}
        </td>
      )}
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ActualShipDate")}>
        {renderEditableCell(job, "ActualShipDate", formatDateToMMDD(job.ActualShipDate) || "")}
      </td>
      {!isNorthPrairieView && (
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ScheduledArrivalDate")}>
          {renderEditableCell(job, "ScheduledArrivalDate", formatDateToMMDD(job.ScheduledArrivalDate) || "")}
        </td>
      )}
    </>
  );
};

// Portal Details columns renderer
export const renderPortalDetailsColumns = (job, filters, handleCellDoubleClick, renderEditableCell) => {
  if (!filters.departments.portalDetails) return null;
  const location = getScheduleLocation(job);
  
  return (
    <>
      <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "SalesRep")}> 
        {renderEditableCell(job, "SalesRep", job.SalesRep || "")}
      </td>
      <td className={location.fromShipTo ? undefined : 'editable-cell'}
        title={location.estimated ? 'Location from general project; Ship To city and state are not available.' : undefined}
        onDoubleClick={location.fromShipTo ? undefined : (e) => handleCellDoubleClick(e, job, "JobLocation")}>
        {location.fromShipTo ? location.value : <>{renderEditableCell(job, "JobLocation", job.JobLocation || "")}{location.estimated && '*'}</>}
      </td>
    </>
  );
};
